/**
 * Показ раздела «Склад» без сервера: `VITE_MOCK_API=1`.
 *
 * Выдуманные находки аудита, подорожания и блюда, ждущие товара, — в форме
 * ответов `ops-api` и `finance-api`. Решения «это он» и «не он» живут в
 * памяти вкладки.
 */

import { ApiError } from '@shell/api';
import type { PortalRequest } from '../../api';
import type { Goods, Matching, PriceChange } from './api';

const now = Date.now();
const day = (offset: number) => new Date(now + offset * 86_400_000).toISOString().slice(0, 10);

const audit = {
  synced_at: new Date(now - 6 * 3_600_000).toISOString(),
  missing: [],
  goods_read: true,
  remains_read: true,
  counts: { units: 3, negatives: 2, duplicates: 2, total: 7 },
  units: [
    { good_id: '1041', name: 'Сливки 33%', problem: 'нет единицы измерения' },
    { good_id: '2210', name: 'Мука пшеничная в/с', problem: 'нет пересчёта для «мешок»' },
    { good_id: '3302', name: 'Лимоны', problem: 'покупают в «ящ», а такой единицы у товара нет' },
  ],
  negatives: [
    { good_id: '5120', name: 'Креветки тигровые 16/20', quantity: '-2.35', unit: 'кг', department: 'Кухня' },
    { good_id: '6011', name: 'Апероль', quantity: '-0.7', unit: 'л', department: 'Бар' },
  ],
  duplicates: [
    { names: ['Сыр Пармезан', 'сыр пармезан'], good_ids: ['7001', '7044'] },
    { names: ['Масло сливочное 82,5 %', 'Масло сливочное 82,5%'], good_ids: ['7102', '7310'] },
  ],
};

function change(
  goods: string,
  unit: string,
  supplier: [number, string],
  before: string,
  after: string,
  invoiceId: number,
  threshold = 15,
): PriceChange {
  const pct = ((Number(after) - Number(before)) / Number(before)) * 100;
  return {
    goods_id: goods,
    goods,
    unit,
    supplier: { id: supplier[0], name: supplier[1] },
    previous: { date: day(-40), price: before },
    last: { date: day(-3), price: after, invoice_id: invoiceId },
    change_pct: pct.toFixed(1),
    above_threshold: pct > threshold,
  };
}

const MILK: [number, string] = [21, 'ООО «Молочный двор»'];
const MEAT: [number, string] = [11, 'ООО «Мираторг-Запад»'];
const VEG: [number, string] = [13, 'Продстар-Торговый Дом'];

const changes = [
  change('Сливки 33%', 'л', MILK, '310.00', '372.00', 301),
  change('Томаты черри', 'кг', VEG, '420.00', '505.00', 306),
  change('Говядина вырезка', 'кг', MEAT, '1850.00', '1960.00', 302),
  change('Моцарелла', 'кг', MILK, '780.00', '805.00', 303),
];

const goods: Goods[] = [
  { goods_id: 'G-101', name: 'Паста карбонара (п/ф)', code: '1101', unit: 'порц' },
  { goods_id: 'G-102', name: 'Паста карбонара', code: null, unit: 'порц' },
  { goods_id: 'G-201', name: 'Тирамису', code: '1201', unit: 'порц' },
  { goods_id: 'G-202', name: 'Тирамису', code: '1202', unit: 'шт' },
  { goods_id: 'G-301', name: 'Лимонад домашний', code: '1301', unit: 'л' },
  { goods_id: 'G-302', name: 'Эспрессо', code: '1302', unit: 'порц' },
];

const matching: Matching = {
  days: 30,
  counts: { dishes: 678, matched: 654, ambiguous: 2, unmatched: 3, rejected_skipped: 1 },
  ambiguous: [
    { item_id: 1, name: 'Паста карбонара', code: '101', sold: { quantity: 212, amount: '169600.00' }, candidates: [goods[0], goods[1]] },
    { item_id: 2, name: 'Тирамису', code: '102', sold: { quantity: 148, amount: '66600.00' }, candidates: [goods[2], goods[3]] },
  ],
  unmatched: [
    { item_id: 3, name: 'Лимонад домашний 1 л', code: '301', sold: { quantity: 64, amount: '32000.00' } },
    { item_id: 4, name: 'Двойной эспрессо', code: '302', sold: { quantity: 90, amount: '21600.00' } },
    { item_id: 5, name: 'Тест печати', code: '999', sold: { quantity: 0, amount: '0.00' } },
  ],
  decisions: [],
};

function decide(itemId: number, goodsId: string, confirm: boolean) {
  const picked = goods.find((g) => g.goods_id === goodsId);
  if (!picked) throw new ApiError(404, 'not found', 'Такого действующего товара на складе нет.');
  const dish =
    matching.ambiguous.find((d) => d.item_id === itemId) ?? matching.unmatched.find((d) => d.item_id === itemId);
  if (!dish) throw new ApiError(404, 'not found', 'Блюдо не найдено.');
  if (confirm) {
    matching.ambiguous = matching.ambiguous.filter((d) => d.item_id !== itemId);
    matching.unmatched = matching.unmatched.filter((d) => d.item_id !== itemId);
    matching.counts.matched += 1;
  } else {
    matching.ambiguous = matching.ambiguous
      .map((d) => (d.item_id === itemId ? { ...d, candidates: d.candidates.filter((c) => c.goods_id !== goodsId) } : d))
      .filter((d) => d.candidates.length > 1);
  }
  matching.counts.ambiguous = matching.ambiguous.length;
  matching.counts.unmatched = matching.unmatched.length;
  matching.decisions = [
    {
      item_id: itemId,
      item_name: dish.name,
      goods_id: goodsId,
      goods_name: picked.name,
      status: confirm ? 'confirmed' : 'rejected',
      decided_at: new Date().toISOString(),
      decided_by: 'Пётр Соколов',
    },
    ...matching.decisions,
  ];
  return {
    message: confirm
      ? `«${dish.name}» связано с товаром «${picked.name}»: себестоимость блюда считается по нему.`
      : `«${picked.name}» больше не будет предлагаться для «${dish.name}».`,
    matching: structuredClone(matching),
  };
}

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  const body = (options.body ?? {}) as Record<string, unknown>;

  if (pathname === '/api/ops/stock/audit') return audit;
  if (pathname === '/api/ops/stock/prices') {
    const all = params.get('all') === 'true';
    const items = changes.filter((row) => all || row.above_threshold);
    return {
      since: day(-(Number(params.get('days')) || 180)),
      days: Number(params.get('days')) || 180,
      threshold_pct: '15',
      all,
      compared: 118,
      count: items.length,
      items,
    };
  }
  if (pathname === '/api/finance/portal/stock/matching') return structuredClone(matching);
  if (pathname === '/api/finance/portal/stock/goods') {
    const q = (params.get('q') ?? '').trim().toLowerCase();
    if (q.length < 2) return [];
    return goods.filter((g) => g.name.toLowerCase().includes(q) || g.code === q).slice(0, 10);
  }
  const decision = /^\/api\/finance\/portal\/stock\/matching\/(\d+)\/(confirm|reject)$/.exec(pathname);
  if (decision) return decide(Number(decision[1]), String(body.goods_id), decision[2] === 'confirm');
  return undefined;
}
